import { useEffect, useState } from 'react';
import { Layout, Button, Avatar, Dropdown, Typography, Space, Badge, Tooltip, Popover } from 'antd';
import { MenuOutlined, UserOutlined, LogoutOutlined, BellOutlined } from '@ant-design/icons';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { SearchAutocomplete } from '../UI/SearchAutocomplete';
import { ThemeToggle } from '../UI/ThemeToggle';
import { TourButton } from '../UI/TourButton';
import { NotificationDropdown } from '../Notification/NotificationDropdown';
import Api from '@/services/api';
import { AlertLog } from '@/types';
import { TenantSwitcher } from '../Admin/TenantSwitcher';
import { TenantIndicator } from './TenantIndicator';

const { Header: AntHeader } = Layout;
const { Text } = Typography;

interface HeaderProps {
  onMenuClick?: () => void;
  showMenuButton?: boolean;
}

export function Header({ onMenuClick, showMenuButton = false }: HeaderProps) {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [avatarSrc, setAvatarSrc] = useState<string | undefined>(undefined);
  const [, setNotifications] = useState<AlertLog[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { user, logout, auditEnabled } = useAuth();
  const navigate = useNavigate();
  const api = new Api();

  const loadNotifications = async () => {
    if (!user) return;
    try {
      const response = await api.getUserNotifications();
      setNotifications(response.notifications || []);
      setUnreadCount(response.unread_count || 0);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  const loadUnreadCount = async () => {
    if (!user) return;
    try {
      const response = await api.get<{ unread_count: number }>('/alerts/notifications/count');
      setUnreadCount(response.unread_count || 0);
    } catch {
      // Silently ignore polling errors to avoid console spam
    }
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Perfil',
    },
    ...(user?.is_admin
      ? [
          {
            type: 'divider' as const,
          },
          {
            label: 'Administração',
            key: 'admin-group',
            children: [
              {
                key: 'admin/alerts',
                label: 'Alertas',
              },
              {
                key: 'admin/aprovacoes',
                label: 'Aprovações de usuários',
              },
              ...(auditEnabled
                ? [
                    {
                      key: 'admin/auditoria',
                      label: 'Auditoria',
                    },
                  ]
                : []),
              {
                key: 'admin/settings/general',
                label: 'Configurações gerais',
              },
              {
                key: 'admin/planos',
                label: 'Planos',
              },
              ...(user?.is_superadmin
                ? [
                    {
                      type: 'divider' as const,
                    },
                    {
                      key: 'superadmin/tenants',
                      label: 'Gerenciar Tenants',
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sair',
      danger: true,
    },
  ];

  const handleUserMenuClick = ({ key }: { key: string }) => {
    switch (key) {
      case 'logout':
        logout().then(() => navigate('/login'));
        break;
      case 'profile':
        navigate('/perfil');
        break;
      case 'admin/settings/general':
        navigate('/admin/settings/general');
        break;
      case 'admin/auditoria':
        navigate('/admin/auditoria');
        break;
      case 'admin/alerts':
        navigate('/admin/alerts');
        break;
      case 'admin/aprovacoes':
        navigate('/admin/aprovacoes');
        break;
      case 'admin/planos':
        navigate('/admin/planos');
        break;
      case 'superadmin/tenants':
        navigate('/superadmin/tenants');
        break;
      case 'settings':
        break;
    }
    setUserMenuOpen(false);
  };

  const roleBadge =
    user?.role_title ||
    (user?.permission === 'admin'
      ? 'Administrador'
      : user?.permission === 'edit_read_delete'
      ? 'Gestão completa'
      : user?.permission === 'edit_read'
      ? 'Atualização'
      : 'Leitura');

  useEffect(() => {
    let active = true;
    let objectUrl: string | undefined;

    setAvatarError(false);

    if (!user?.profile_photo_url) {
      setAvatarSrc(undefined);
      return () => {
        active = false;
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
      };
    }

    api
      .getProfilePhotoBlob()
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setAvatarSrc(objectUrl);
      })
      .catch(() => {
        if (active) {
          setAvatarError(true);
          setAvatarSrc(undefined);
        }
      });

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [user?.profile_photo_url, user?.profile_photo_updated_at]);

  useEffect(() => {
    loadNotifications();
    // Poll unread count every 30 seconds
    const interval = setInterval(loadUnreadCount, 30_000);
    return () => clearInterval(interval);
  }, [user]);

  const avatarDisplaySrc = avatarSrc && !avatarError ? avatarSrc : undefined;

  return (
    <AntHeader
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        padding: '0 24px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, height: 64 }}>
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={onMenuClick}
          style={{
            fontSize: 18,
            display: showMenuButton ? 'inline-flex' : 'none',
          }}
          aria-label="Abrir menu"
        />

        <SearchAutocomplete />

        <Space size={16} align="center" style={{ marginLeft: 'auto', flexShrink: 0 }}>
          <TourButton />
          <ThemeToggle />

          <Popover
            content={
              <div style={{ width: 380, maxHeight: 500, overflow: 'auto' }}>
                <NotificationDropdown onNotificationsUpdated={loadNotifications} onClose={() => setNotificationsOpen(false)} />
              </div>
            }
            title={null}
            trigger="click"
            placement="bottomRight"
            open={notificationsOpen}
            onOpenChange={setNotificationsOpen}
          >
            <Tooltip title="Notificações">
              <Badge count={unreadCount} size="small">
                <Button type="text" icon={<BellOutlined />} aria-label="Notificações" />
              </Badge>
            </Tooltip>
          </Popover>

          {/* Tenant Switcher & Indicator (super admin vê seletor + tag; demais veem só a tag) */}
          {user?.is_superadmin && <TenantSwitcher />}
          <TenantIndicator />

          {!user ? (
            <Button type="link" onClick={() => navigate('/login')}>
              Entrar
            </Button>
          ) : (
            <Dropdown
              menu={{
                items: userMenuItems,
                onClick: handleUserMenuClick,
              }}
              placement="bottomRight"
              trigger={['click']}
              open={userMenuOpen}
              onOpenChange={setUserMenuOpen}
            >
              <Button
                type="text"
                style={{ padding: '4px 0' }}
                aria-label="Abrir menu do usuário"
                data-testid="user-menu"
              >
                <Space align="center" size={8}>
                  <Avatar
                    src={avatarDisplaySrc}
                    icon={!avatarDisplaySrc ? <UserOutlined /> : undefined}
                    size={36}
                    style={{
                      backgroundColor: '#1f4bd8',
                    }}
                    onError={() => {
                      setAvatarError(true);
                      return true;
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Text strong style={{ fontSize: 14, lineHeight: 1.2 }}>
                      {user?.name || 'Usuário'}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.2 }}>
                      {roleBadge}
                    </Text>
                  </div>
                </Space>
              </Button>
            </Dropdown>
          )}
        </Space>
      </div>
    </AntHeader>
  );
}
